import { askCancel } from '@/lib/cancelReason';
import { cur } from '@/lib/currency';
import { useEffect, useState, useRef, useMemo, memo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ExportButtons } from '@/components/ExportButtons';;
import PageTour from '@/components/PageTour';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Plus, Trash2, X, FileText, DollarSign, Printer, Image, Upload, Edit, ShieldCheck, FileDown, Mail, Search } from 'lucide-react';
import { exportInvoiceToPDF, exportInvoicesMergedPDF, invoicePdfBase64 } from '@/lib/pdfExport';
import { QRCodeSVG } from 'qrcode.react';
import api from '@/lib/api';
import { getCompanySettings, getTaxRates } from '@/lib/configCache';
import { useBranchStore } from '@/stores';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';
import { BranchMultiSelect } from '@/components/BranchMultiSelect';
import { ReportHeader } from '@/components/reports/ReportUI';
import { PageSizeSelector } from '@/components/common/PageSizeSelector';
import { DateQuickFilter } from '@/components/DateQuickFilter';
import { useTaxConfig, computeVat } from '@/hooks/useTaxConfig';
import BranchTaxBadge from '@/components/BranchTaxBadge';
import { localToday } from '@/lib/dateRange';
import { SSoTCompanyInfoBanner } from '@/components/common/SSoTCompanyInfoBanner';
import { ZatcaRowIcon } from '@/components/zatca/ZatcaRowIcon';

/* iter927 — 'All Invoices' table extracted into a React.memo component so
 * typing in the invoice form does NOT re-render 100+ rows. The comparator
 * checks only data props; the `h` handlers object is intentionally ignored
 * (its closures refresh whenever any compared prop changes). */
const InvoicesTable = memo(function InvoicesTable({ filtered, selectedIds, salesById, zatcaPhase2, companySettings, uploadingId, emailingId, mergingPdf, h }) {
  const getCreditRemaining = (inv) => {
    if (!inv.sale_id) return 0;
    const sale = salesById.get(inv.sale_id);
    if (!sale) return 0;
    return (sale.credit_amount || 0) - (sale.credit_received || 0);
  };
  return (
    <Card className="border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-2">
        <CardTitle className="font-outfit">All Invoices</CardTitle>
        {selectedIds.length > 0 && (
          <Button size="sm" variant="outline" className="h-8 text-xs text-info border-info/30 hover:bg-info/10" disabled={mergingPdf} onClick={h.handleMergedPdf} data-testid="merge-pdf-btn" title="Generate one merged audit PDF for the selected invoices">
            <FileDown size={14} className="mr-1" />{mergingPdf ? 'Merging…' : `Merged PDF (${selectedIds.length})`}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="invoices-table-2">
            <thead><tr className="border-b border-border">
              <th className="p-3 w-8"><input type="checkbox" className="h-4 w-4 accent-primary cursor-pointer" checked={filtered.length > 0 && selectedIds.length === filtered.length} onChange={() => h.setSelectedIds((p) => (p.length === filtered.length ? [] : filtered.map((i) => i.id)))} data-testid="invoice-select-all" /></th>
              <th className="text-left p-3 font-medium text-sm">Invoice #</th>
              <th className="text-left p-3 font-medium text-sm hidden sm:table-cell">Date</th>
              <th className="text-left p-3 font-medium text-sm">Customer</th>
              <th className="text-center p-3 font-medium text-sm hidden md:table-cell">Items</th>
              <th className="text-right p-3 font-medium text-sm hidden md:table-cell">Subtotal</th>
              <th className="text-right p-3 font-medium text-sm hidden lg:table-cell">Discount</th>
              <th className="text-right p-3 font-medium text-sm">Total</th>
              <th className="text-right p-3 font-medium text-sm hidden lg:table-cell">VAT</th>
              <th className="text-left p-3 font-medium text-sm hidden sm:table-cell">Payment</th>
              <th className="text-center p-3 font-medium text-sm hidden lg:table-cell">Img</th>
              <th className="text-right p-3 font-medium text-sm hidden md:table-cell">Credit Due</th>
              <th className="text-right p-3 font-medium text-sm">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map(inv => {
                const creditRem = getCreditRemaining(inv);
                return (
                <tr key={inv.id} className="border-b border-border hover:bg-secondary/50" data-testid="invoice-row">
                  <td className="p-3 w-8"><input type="checkbox" className="h-4 w-4 accent-primary cursor-pointer" checked={selectedIds.includes(inv.id)} onChange={() => h.toggleSelect(inv.id)} data-testid={`invoice-select-${inv.id}`} /></td>
                  <td className="p-3 text-sm font-medium text-primary"><span className="inline-flex items-center gap-1.5">{inv.invoice_number}<ZatcaRowIcon status={inv.zatca_status} /></span></td>
                  <td className="p-3 text-sm hidden sm:table-cell">{format(new Date(inv.date), 'MMM dd, yyyy')}</td>
                  <td className="p-3 text-sm">{inv.customer_name || 'Walk-in'}</td>
                  <td className="p-3 text-center hidden md:table-cell"><Badge variant="secondary">{inv.items?.length || 0}</Badge></td>
                  <td className="p-3 text-sm text-right hidden md:table-cell"> {cur()} {inv.subtotal.toFixed(2)}</td>
                  <td className="p-3 text-sm text-right text-error hidden lg:table-cell">{inv.discount > 0 ? `-${cur()} ${inv.discount.toFixed(2)}` : '-'}</td>
                  <td className="p-3 text-sm text-right font-bold"> {cur()} {(inv.total_with_vat || inv.total)?.toFixed(2)}</td>
                  <td className="p-3 text-sm text-right text-info hidden lg:table-cell">{(inv.vat_amount || 0) > 0 ? `${cur()} ${inv.vat_amount.toFixed(2)}` : '-'}</td>
                  <td className="p-3 hidden sm:table-cell"><Badge className={inv.payment_mode === 'cash' ? 'bg-cash/20 text-cash' : inv.payment_mode === 'bank' ? 'bg-bank/20 text-bank' : 'bg-credit/20 text-credit'}>{inv.payment_mode}</Badge></td>
                  <td className="p-3 text-center hidden lg:table-cell">
                    {inv.image_url ? (
                      <button onClick={() => h.setViewImage(inv)} className="inline-flex items-center justify-center" data-testid={`view-image-${inv.id}`}>
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${inv.image_url}`} alt="" className="w-8 h-8 rounded object-cover border border-border hover:ring-2 hover:ring-orange-300 transition-all" />
                      </button>
                    ) : (
                      <label className="cursor-pointer inline-flex items-center justify-center">
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && h.handleImageUpload(inv.id, e.target.files[0])} />
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded border border-dashed border-border text-muted-foreground hover:border-orange-400 hover:text-orange-500 transition-all ${uploadingId === inv.id ? 'animate-pulse' : ''}`} data-testid={`upload-image-${inv.id}`}>
                          <Upload size={14} />
                        </span>
                      </label>
                    )}
                  </td>
                  <td className="p-3 text-right hidden md:table-cell">{creditRem > 0 ? <span className="font-bold text-warning"> {cur()} {creditRem.toFixed(2)}</span> : <span className="text-muted-foreground">-</span>}</td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end flex-wrap">
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => h.openEditItems(inv)} data-testid="edit-items-btn">
                        <Edit size={14} className="mr-1" />Items
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="print-invoice-btn"
                        onClick={async () => {
                          h.setPrintInvoice(inv);
                          try {
                            const { data: qr } = await api.get(`/invoices/${inv.id}/zatca-qr`);
                            h.setQrData(qr.qr_data);
                          } catch { h.setQrData(null); }
                        }}>
                        <Printer size={14} className="mr-1" />Print
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`invoice-pdf-btn-${inv.id}`} title="Download branded PDF invoice with ZATCA QR"
                        onClick={async () => {
                          let qr = null;
                          try { const { data } = await api.get(`/invoices/${inv.id}/zatca-qr`); qr = data.qr_data; } catch { /* no QR available */ }
                          await exportInvoiceToPDF(inv, companySettings, qr, cur());
                        }}>
                        <FileDown size={14} className="mr-1" />PDF
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`invoice-email-btn-${inv.id}`} title="Email the branded PDF invoice to the customer" disabled={emailingId === inv.id} onClick={() => h.handleEmailInvoice(inv)}>
                        <Mail size={14} className={`mr-1 ${emailingId === inv.id ? 'animate-pulse' : ''}`} />{emailingId === inv.id ? 'Sending…' : 'Email'}
                      </Button>
                      {(() => {
                        const zst = (inv.zatca_status || '').toUpperCase();
                        if (zst === 'REPORTED' || zst === 'CLEARED') {
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400" title={`ZATCA ${zst}`} data-testid={`zatca-submitted-${inv.id}`}>
                              <ShieldCheck size={12} /> {zst === 'CLEARED' ? 'Cleared' : 'Reported'}
                            </span>
                          );
                        }
                        if (!zatcaPhase2.reporting) return null;
                        return (
                          <Button size="sm" variant="outline" className="h-8 text-xs text-violet-700 border-violet-200 hover:bg-violet-50 dark:text-violet-400 dark:border-violet-900" onClick={() => h.submitZatca(inv)} data-testid={`zatca-submit-${inv.id}`}>
                            <ShieldCheck size={12} className="mr-1" /> Submit to ZATCA
                          </Button>
                        );
                      })()}
                      {creditRem > 0 && (
                        <Button size="sm" variant="outline" onClick={() => { h.setReceivingInvoice({ ...inv, credit_remaining: creditRem }); h.setReceiveData({ payment_mode: 'cash', amount: '', discount: '' }); h.setShowReceiveDialog(true); }} className="h-8 text-xs" data-testid="receive-invoice-credit">
                          <DollarSign size={14} className="mr-1" />Receive
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => h.handleDelete(inv.id)} className="h-8 text-error hover:text-error" aria-label="Delete invoice" data-testid={`invoice-delete-${inv.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={12} className="p-8 text-center text-muted-foreground">No invoices yet</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}, (prev, next) =>
  prev.filtered === next.filtered &&
  prev.selectedIds === next.selectedIds &&
  prev.salesById === next.salesById &&
  prev.zatcaPhase2 === next.zatcaPhase2 &&
  prev.companySettings === next.companySettings &&
  prev.uploadingId === next.uploadingId &&
  prev.emailingId === next.emailingId &&
  prev.mergingPdf === next.mergingPdf
);


export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const { branches, fetchBranches: _fetchBr } = useBranchStore();
  const [customers, setCustomers] = useState([]);
  const [masterItems, setMasterItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [receivingInvoice, setReceivingInvoice] = useState(null);
  const [receiveData, setReceiveData] = useState({ payment_mode: 'cash', amount: '', discount: '' });
  const [newItem, setNewItem] = useState({ name: '', unit_price: '', category: '' });
  const [customerSearch, setCustomerSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: 'all' });
  const [listSearch, setListSearch] = useState('');
  const [invPageSize, setInvPageSize] = useState(50);
  const [printInvoice, setPrintInvoice] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [companySettings, setCompanySettings] = useState({});
  const [scanning, setScanning] = useState(false);
  const [formData, setFormData] = useState({
    branch_id: '', customer_id: '', payment_mode: 'cash', discount: '',
    date: localToday(), notes: '', tax_rate_id: '',
    items: [{ description: '', quantity: 1, unit_price: '', tax_rate_id: '', master_item_id: '' }],
    apply_vat: null, vat_inclusive: null, invoice_number_ref: '', due_date: '',
  });
  const [taxRates, setTaxRates] = useState([]);
  const [zatcaPhase2, setZatcaPhase2] = useState({ reporting: false });
  const { taxConfig } = useTaxConfig(formData.branch_id);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin' || user.role === 'manager';

  // iter913 — bulk selection for merged audit PDF + per-row email
  const [selectedIds, setSelectedIds] = useState([]);
  const [mergingPdf, setMergingPdf] = useState(false);
  const [emailingId, setEmailingId] = useState(null);
  const toggleSelect = (id) => setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const fetchQr = async (id) => {
    try { const { data } = await api.get(`/invoices/${id}/zatca-qr`); return data.qr_data; } catch { return null; }
  };

  const handleMergedPdf = async () => {
    const invs = filtered.filter((i) => selectedIds.includes(i.id));
    if (!invs.length) { toast.info('Select invoices first'); return; }
    setMergingPdf(true);
    try {
      const qrMap = {};
      await Promise.all(invs.map(async (i) => { qrMap[i.id] = await fetchQr(i.id); }));
      await exportInvoicesMergedPDF(invs, companySettings, qrMap, cur());
    } finally { setMergingPdf(false); }
  };

  const handleEmailInvoice = async (inv) => {
    setEmailingId(inv.id);
    try {
      const qr = await fetchQr(inv.id);
      const pdf_base64 = await invoicePdfBase64(inv, companySettings, qr, cur());
      const { data } = await api.post(`/invoices/${inv.id}/email`, { pdf_base64, filename: `Invoice_${inv.invoice_number || inv.id}.pdf` });
      toast.success(`Invoice emailed to ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to email invoice');
    } finally { setEmailingId(null); }
  };

  useEffect(() => { fetchData(); }, []);

  // iter936 — Server-side filtering. Combining Branch + Date + Search used
  // to return empty because the filter only saw the loaded 50 rows; now the
  // query is pushed to the API and the list resets to page 1 on any change.
  const invoiceQS = (page) => {
    const p = new URLSearchParams({ limit: String(Math.min(invPageSize, 200)), page: String(page) });
    if (branchFilter.length) p.set('branch_ids', branchFilter.join(','));
    if (dateFilter.start_date) p.set('start_date', dateFilter.start_date);
    if (dateFilter.end_date) p.set('end_date', dateFilter.end_date);
    if (listSearch.trim()) p.set('search', listSearch.trim());
    return p.toString();
  };

  const refetchInvoices = async () => {
    try {
      const { data } = await api.get(`/invoices?${invoiceQS(1)}`);
      setInvoices(data?.data || data || []);
      setInvoiceTotal(data?.total || (data?.data || []).length);
      setPageNum(1);
    } catch { toast.error('Failed to filter invoices'); }
  };

  // Refetch whenever a filter changes (skips the very first mount — fetchData
  // above already loaded page 1).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    refetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter, dateFilter, listSearch, invPageSize]);

  // iter930 — Pagination: 50 per page (was 100), appended via "Load More".
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const loadMoreInvoices = async () => {
    try {
      const next = pageNum + 1;
      const { data } = await api.get(`/invoices?${invoiceQS(next)}`);
      // iter933 — de-dupe by id: a locally prepended invoice shifts server
      // paging, so page N can re-return boundary rows we already show.
      setInvoices(prev => {
        const seen = new Set(prev.map(i => i.id));
        return [...prev, ...(data?.data || []).filter(i => !seen.has(i.id))];
      });
      setPageNum(next);
    } catch { toast.error('Failed to load more invoices'); }
  };

  const fetchData = async () => {
    try {
      const [iRes, , cRes, itemsRes, coRes, trRes, zRes] = await Promise.all([api.get(`/invoices?${invoiceQS(1)}`), Promise.resolve({ data: [] }), api.get('/customers'), api.get('/items'), getCompanySettings().then(d => ({ data: d })).catch(() => ({ data: {} })), getTaxRates().then(d => ({ data: d })).catch(() => ({ data: [] })), api.get('/zatca-p2/status').catch(() => ({ data: { reporting: false } }))]);
      setInvoices(iRes.data?.data || iRes.data || []); setCustomers(cRes.data?.data || cRes.data || []); setMasterItems(itemsRes.data);
      setInvoiceTotal(iRes.data?.total || (iRes.data?.data || []).length);
      setPageNum(1);
      if (coRes.data) setCompanySettings(coRes.data);
      setTaxRates(trRes.data || []);
      setZatcaPhase2(zRes.data || { reporting: false });
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  // STEP 2 — route the Invoice action through the canonical shared ZATCA
  // Phase-2 submission (same EGS chain POS uses). Replaces the legacy local
  // self-sign (/zatca-phase2/sign-invoice). Idempotent + chain-safe server-side.
  const submitZatca = async (inv) => {
    const tId = toast.loading('Submitting to ZATCA…');
    try {
      const r = await api.post(`/invoices/${inv.id}/zatca-submit`);
      const st = (r.data?.status || '').toUpperCase();
      if (st === 'CLEARED') toast.success('Invoice cleared by ZATCA ✓', { id: tId });
      else if (st === 'REPORTED') toast.success('Invoice reported to ZATCA ✓', { id: tId });
      else toast.success(r.data?.message || 'Submitted to ZATCA', { id: tId });
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'ZATCA submission failed');
      if (e.response?.status === 409 && /BLOCKED/i.test(msg)) {
        toast.error('ZATCA chain is blocked — resolve it in ZATCA Phase 2 before resubmitting.', { id: tId });
      } else {
        toast.error(msg, { id: tId });
      }
    }
  };

  const addItem = () => setFormData({ ...formData, items: [...formData.items, { description: '', quantity: 1, unit_price: '', tax_rate_id: formData.tax_rate_id || '', master_item_id: '' }] });
  const removeItem = (i) => setFormData({ ...formData, items: formData.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, val) => {
    const items = [...formData.items];
    items[i][field] = val;
    setFormData({ ...formData, items });
  };
  const selectMasterItem = (i, itemId) => {
    const mi = masterItems.find(m => m.id === itemId);
    if (mi) {
      const items = [...formData.items];
      items[i] = { description: mi.name, quantity: items[i].quantity || 1, unit_price: mi.unit_price, master_item_id: mi.id };
      setFormData({ ...formData, items });
    }
  };
  const handleAddMasterItem = async () => {
    if (!newItem.name) return;
    try {
      await api.post('/items', { ...newItem, unit_price: parseFloat(newItem.unit_price) || 0 });
      toast.success('Item added');
      setNewItem({ name: '', unit_price: '', category: '' });
      setShowItemForm(false);
      const res = await api.get('/items');
      setMasterItems(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const filteredCustomers = customers.filter(c => !customerSearch || c.name.toLowerCase().includes(customerSearch.toLowerCase()));

  const handleOcrScan = async (file) => {
    if (!file) return;
    setScanning(true);
    toast.loading('Scanning invoice with AI...', { id: 'ocr-scan' });
    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(file);
      });
      const { data } = await api.post('/invoices/ocr-scan', { image: base64 });
      // Auto-fill form
      const items = (data.items || []).map(item => ({
        description: item.description || item.name || '',
        quantity: item.quantity || 1,
        unit_price: item.unit_price || item.total || ''
      }));
      if (items.length === 0) items.push({ description: '', quantity: 1, unit_price: '' });
      setFormData(prev => ({
        ...prev,
        items,
        discount: data.discount || '',
        notes: data.notes || prev.notes,
        payment_mode: data.payment_mode || prev.payment_mode,
        date: data.date || prev.date,
        customer_id: data.customer_name ? (customers.find(c => c.name.toLowerCase().includes((data.customer_name || '').toLowerCase()))?.id || prev.customer_id) : prev.customer_id,
      }));
      toast.success(`Scanned ${items.length} items from invoice`, { id: 'ocr-scan' });
      setShowForm(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OCR scan failed', { id: 'ocr-scan' });
    } finally {
      setScanning(false);
    }
  };

  const calcTotals = () => {
    let subtotal = 0;
    const itemTotals = formData.items.map(item => {
      const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      subtotal += lineTotal;
      return { lineTotal };
    });
    const discount = parseFloat(formData.discount) || 0;
    const total = subtotal - discount;
    // Effective VAT from unified tax config (branch override > tenant default)
    const applyVat = formData.apply_vat !== null ? formData.apply_vat : !!taxConfig?.vat_enabled;
    const vatInclusive = formData.vat_inclusive !== null ? formData.vat_inclusive : !!taxConfig?.vat_inclusive;
    const vatRate = applyVat ? (taxConfig?.vat_rate || 0) : 0;
    const { net, vat, gross } = computeVat({ amount: total, rate: vatRate, inclusive: vatInclusive, enabled: applyVat });
    return {
      subtotal, discount, total: net, vatEnabled: applyVat, vatRate,
      vatAmount: vat, totalWithVat: gross, vatInclusive, itemTotals,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const totals = calcTotals();
    if (totals.subtotal === 0) { toast.error('Add at least one item'); return; }
    try {
      const payload = {
        branch_id: formData.branch_id || null,
        customer_id: formData.customer_id || null,
        items: formData.items.filter(i => i.description && parseFloat(i.unit_price) > 0).map(i => ({
          description: i.description,
          quantity: parseFloat(i.quantity) || 1,
          unit_price: parseFloat(i.unit_price),
          tax_rate: totals.vatRate,
        })),
        discount: totals.discount,
        tax_rate: totals.vatRate,
        tax_amount: totals.vatAmount,
        payment_mode: formData.payment_mode,
        date: new Date(formData.date).toISOString(),
        notes: formData.notes || null,
        apply_vat: totals.vatEnabled,
        vat_rate_override: totals.vatRate,
        vat_inclusive: totals.vatInclusive,
        invoice_number_ref: formData.invoice_number_ref || null,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
      };
      const { data: created } = await api.post('/invoices', payload);
      toast.success('Invoice created & sale recorded');
      setShowForm(false);
      setFormData({
        branch_id: '', customer_id: '', payment_mode: 'cash', discount: '',
        date: localToday(), notes: '', tax_rate_id: '',
        items: [{ description: '', quantity: 1, unit_price: '', tax_rate_id: '', master_item_id: '' }],
        apply_vat: null, vat_inclusive: null, invoice_number_ref: '', due_date: '',
      });
      // iter927 — Smart Save: prepend the created invoice locally instead of
      // refetching the whole list.
      if (created?.id) {
        setInvoices(prev => [created, ...prev]);
        setInvoiceTotal(t => t + 1);
      } else {
        fetchData();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice? Posted, paid or ZATCA-reported invoices are protected and will not be deleted.')) return;
    const del = (params) => api.delete(`/invoices/${id}`, { params });
    try {
      let r;
      try { r = await del(); } catch (err) {
        if (err.response?.status !== 400 || !/reason/i.test(err.response?.data?.detail || '')) throw err;
        const p = askCancel('Cancel this invoice?', true);
        if (!p) return;
        r = await del(p);
      }
      toast.success(r.data?.message || 'Deleted'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleReceiveCredit = async (e) => {
    e.preventDefault();
    if (!receivingInvoice?.sale_id) return;
    try {
      const amount = parseFloat(receiveData.amount) || 0;
      const discount = parseFloat(receiveData.discount) || 0;
      await api.post(`/sales/${receivingInvoice.sale_id}/receive-credit`, { payment_mode: receiveData.payment_mode, amount, discount });
      toast.success(`${cur()} ${(amount + discount).toFixed(2)} settled`);
      setShowReceiveDialog(false);
      setReceiveData({ payment_mode: 'cash', amount: '', discount: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Get credit remaining for an invoice by checking its linked sale
  const [salesData, setSalesData] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const [viewImage, setViewImage] = useState(null);
  const [editInvoice, setEditInvoice] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const imageInputRef = useRef(null);
  useEffect(() => {
    api.get('/sales').then(r => setSalesData(r.data?.data || r.data || [])).catch(() => {});
  }, [invoices]);
  // iter927 — Fast lookup: Map instead of scanning salesData for every row.
  const salesById = useMemo(() => new Map(salesData.map(s => [s.id, s])), [salesData]);
  // iter927 — Memoised so the table's React.memo comparator only fires when
  // the underlying data actually changes (typing in the form won't).
  // iter936 — Server already applied branch/date/search filters, so the
  // list is a straight passthrough (no client-side narrowing over a partial
  // page, which was the empty-results bug).
  const filtered = invoices;

  const handleImageUpload = async (invoiceId, file) => {
    if (!file) return;
    setUploadingId(invoiceId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/invoices/${invoiceId}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded to invoice');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteImage = async (invoiceId) => {
    try {
      await api.delete(`/invoices/${invoiceId}/image`);
      toast.success('Image removed');
      fetchData();
    } catch {
      toast.error('Failed to remove image');
    }
  };

  const openEditItems = (inv) => {
    setEditInvoice(inv);
    setEditItems((inv.items || []).map(item => ({ ...item })));
  };

  const removeEditItem = (idx) => {
    if (editItems.length <= 1) { toast.error('Invoice must have at least one item'); return; }
    setEditItems(editItems.filter((_, i) => i !== idx));
  };

  const saveEditItems = async () => {
    if (!editInvoice || editItems.length === 0) return;
    try {
      await api.put(`/invoices/${editInvoice.id}/items`, { items: editItems });
      toast.success('Invoice items updated');
      setEditInvoice(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  const totals = calcTotals();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3">
          <ReportHeader icon={FileText} title={<span data-testid="invoices-title">Invoices</span>} subtitle="Create invoices with items — auto-added as sales" testId="invoices-header" />
          <div className="flex gap-2 items-center flex-wrap">
            <PageTour storageKey="tour_invoices" steps={[
              { target: '[data-testid="create-invoice-btn"]', title: 'New Invoice', content: 'Create invoices with line items. Invoices auto-record as sales when submitted.' },
              { target: '[data-testid="ocr-scan-btn"]', title: 'AI Scan', content: 'Upload a photo of a paper invoice and AI will extract the details automatically.' },
              { target: '[data-testid="invoices-table"]', title: 'Invoice List', content: 'View all invoices with status tracking. Print, share, or upload receipt images.' },
            ]} />
            <BranchMultiSelect value={branchFilter} onChange={setBranchFilter} branches={branches} testid="invoices-branch-multiselect" />
            <DateQuickFilter onFilterChange={(range) => setDateFilter({ start_date: range?.start || null, end_date: range?.end || null })} />
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder="Search invoice # / customer…"
                className="h-9 w-56 pl-8 rounded-full"
                data-testid="invoice-list-search"
              />
            </div>
            <Button onClick={() => setShowForm(!showForm)} className="rounded-full" size="sm" data-testid="create-invoice-btn-2"><Plus size={16} className="mr-1" />New Invoice</Button>
            <ExportButtons branchIds={branchFilter} dataType="invoices" label="Invoices" startDate={dateFilter?.start_date} endDate={dateFilter?.end_date} />
            <label className="cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleOcrScan(e.target.files[0])} />
              <Button variant="outline" className="rounded-full" size="sm" asChild disabled={scanning} data-testid="ocr-scan-btn-2">
                <span>{scanning ? 'Scanning...' : <><Image size={16} className="mr-1" />Scan Invoice (AI)</>}</span>
              </Button>
            </label>
          </div>
        </div>

        {/* iter690 — SSoT banner: Company VAT / CR / address flow from
            Branding Settings, not re-entered on every invoice. */}
        <SSoTCompanyInfoBanner scopeLabel="Invoice header" />

        {showForm && (
          <Card className="border-border" data-testid="invoice-form">
            <CardHeader><CardTitle className="font-outfit">Create Invoice</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div><Label>Branch *</Label>
                    <Select value={formData.branch_id || "none"} onValueChange={(v) => setFormData({ ...formData, branch_id: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">No Branch</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                    {formData.branch_id && (
                      <div className="mt-1.5">
                        <BranchTaxBadge branchId={formData.branch_id} compact />
                      </div>
                    )}
                  </div>
                  <div><Label>Customer</Label>
                    <SearchableSelect
                      items={[{ id: '', name: 'Walk-in' }, ...customers.map(c => ({ id: c.id, name: c.ref ? `${c.name} (${c.ref})` : c.name }))]}
                      value={formData.customer_id}
                      onChange={(v) => setFormData({ ...formData, customer_id: v })}
                      placeholder="Search customer by name..."
                      className="mt-1"
                    />
                  </div>
                  <div><Label>Payment Mode</Label>
                    <Select value={formData.payment_mode} onValueChange={(v) => setFormData({ ...formData, payment_mode: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem><SelectItem value="credit">Credit</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} /></div>
                </div>
                {/* Invoice-level Tax Rate */}
                {taxRates.length > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-info/10 rounded-lg border border-info/30">
                    <Label className="text-xs font-semibold text-info whitespace-nowrap">Invoice Tax:</Label>
                    <Select value={formData.tax_rate_id || "none"} onValueChange={(v) => setFormData({ ...formData, tax_rate_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="w-52 h-8 text-xs" data-testid="invoice-tax-select"><SelectValue placeholder="No Tax" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" className="text-xs">No Tax</SelectItem>
                        {taxRates.map(tr => (
                          <SelectItem key={tr.id} value={tr.id} className="text-xs">{tr.name} ({tr.rate}%)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-[11px] text-info">Applied to all items without per-item tax</span>
                  </div>
                )}

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <Label>Items</Label>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={addItem} className="rounded-full"><Plus size={14} className="mr-1" />Add Row</Button>
                      {isAdmin && <Button type="button" size="sm" variant="outline" onClick={() => setShowItemForm(!showItemForm)} className="rounded-full text-primary"><Plus size={14} className="mr-1" />New Product</Button>}
                    </div>
                  </div>
                  {showItemForm && isAdmin && (
                    <div className="flex gap-2 items-end p-3 bg-primary/5 rounded-lg border border-primary/20 mb-3">
                      <div className="flex-1"><Label className="text-xs">Product Name</Label><Input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} placeholder="Product name" className="h-8" /></div>
                      <div className="w-28"><Label className="text-xs">Price</Label><Input type="number" step="0.01" value={newItem.unit_price} onChange={(e) => setNewItem({ ...newItem, unit_price: e.target.value })} placeholder="0.00" className="h-8" /></div>
                      <Button type="button" size="sm" onClick={handleAddMasterItem} className="h-8 rounded-full">Save</Button>
                    </div>
                  )}
                  {masterItems.length > 0 && (
                    <div className="mb-3 p-3 bg-secondary/30 rounded-lg border">
                      <p className="text-xs text-muted-foreground mb-2">Click items to add to invoice:</p>
                      <div className="flex gap-2 flex-wrap">
                        {masterItems.filter(m => m.active !== false).map((m, idx) => {
                          const colors = ['bg-primary/10 border-primary/30 text-primary hover:bg-primary/20', 'bg-success/10 border-success/30 text-success hover:bg-success/20', 'bg-info/10 border-info/30 text-info hover:bg-info/20', 'bg-warning/10 border-warning/30 text-warning hover:bg-warning/20', 'bg-error/10 border-error/30 text-error hover:bg-error/20', 'bg-cash/10 border-cash/30 text-cash hover:bg-cash/20', 'bg-bank/10 border-bank/30 text-bank hover:bg-bank/20'];
                          const isSelected = formData.items.some(i => i.description === m.name);
                          return (
                            <Button key={m.id} type="button" size="sm" variant="outline"
                              className={`h-9 text-xs rounded-full border-2 transition-all ${isSelected ? 'ring-2 ring-primary ring-offset-1 font-bold' : ''} ${colors[idx % colors.length]}`}
                              data-testid={`quick-item-SR {m.id}`}
                              onClick={() => setFormData({ ...formData, items: [...formData.items.filter(i => i.description), { description: m.name, quantity: 1, unit_price: m.unit_price }] })}>
                              {m.name} - {cur()} {m.unit_price}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead><tr className="bg-secondary/50 border-b">
                          <th className="text-left p-2 text-xs font-medium">Description</th>
                          <th className="text-center p-2 text-xs font-medium w-20">Qty</th>
                          <th className="text-right p-2 text-xs font-medium w-28">Unit Price</th>
                          <th className="text-right p-2 text-xs font-medium w-28">Total</th>
                          <th className="w-10"></th>
                        </tr></thead>
                        <tbody>
                          {formData.items.map((item, i) => (
                            <tr key={i} className="border-b">
                              <td className="p-1">
                                {masterItems.length > 0 ? (
                                  <SearchableSelect
                                    items={masterItems.filter(m => m.active !== false).map(m => ({
                                      id: m.id, name: `${m.name} — ${cur()} ${m.unit_price}`, raw: m,
                                    }))}
                                    value={item.master_item_id || ''}
                                    onChange={(v) => selectMasterItem(i, v)}
                                    placeholder={item.description || 'Search item…'}
                                    className="h-9"
                                  />
                                ) : (
                                  <Input value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} placeholder="Item description" className="h-8 border-0" data-testid={`item-desc-${i}`} />
                                )}
                              </td>
                              <td className="p-1"><Input type="number" value={item.quantity} onChange={(e) => updateItem(i, 'quantity', e.target.value)} className="h-8 border-0 text-center" /></td>
                              <td className="p-1"><Input type="number" step="0.01" value={item.unit_price} onChange={(e) => updateItem(i, 'unit_price', e.target.value)} placeholder="0.00" className="h-8 border-0 text-right" data-testid={`item-price-${i}`} /></td>
                              <td className="p-2 text-right text-sm font-medium"> {cur()} {((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}</td>
                              <td className="p-1">{formData.items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(i)} className="h-6 w-6 p-0 text-error"><X size={14} /></Button>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="p-3 bg-secondary/30 space-y-1 text-sm">
                      <div className="flex justify-between"><span>Subtotal:</span><span className="font-medium"> {cur()} {totals.subtotal.toFixed(2)}</span></div>
                      <div className="flex items-center gap-2"><span>Discount:</span><Input type="number" step="0.01" value={formData.discount} onChange={(e) => setFormData({ ...formData, discount: e.target.value })} className="h-7 w-24 text-right" placeholder="0.00" data-testid="invoice-discount" /></div>

                      {/* VAT controls — unified from Tax Config */}
                      <div className="mt-2 pt-2 border-t border-border/60 space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="apply-vat-toggle">
                            <input
                              type="checkbox"
                              checked={totals.vatEnabled}
                              onChange={(e) => setFormData({ ...formData, apply_vat: e.target.checked })}
                              className="w-3.5 h-3.5 rounded"
                            />
                            <span className="font-medium">Apply VAT ({taxConfig?.vat_rate || 15}% {taxConfig?.country_name || 'Default'})</span>
                          </label>
                          {totals.vatEnabled && (
                            <div className="flex items-center gap-1 text-[11px]">
                              <span className="text-muted-foreground">Price is:</span>
                              <button
                                type="button"
                                onClick={() => setFormData({ ...formData, vat_inclusive: false })}
                                className={`px-2 py-1 rounded ${!totals.vatInclusive ? 'bg-info text-white' : 'bg-muted text-muted-foreground'}`}
                                data-testid="vat-exclusive-btn"
                              >Exclusive</button>
                              <button
                                type="button"
                                onClick={() => setFormData({ ...formData, vat_inclusive: true })}
                                className={`px-2 py-1 rounded ${totals.vatInclusive ? 'bg-info text-white' : 'bg-muted text-muted-foreground'}`}
                                data-testid="vat-inclusive-btn"
                              >Inclusive</button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-between"><span>{totals.vatInclusive ? 'Net (before VAT):' : 'Net:'}</span><span className="font-medium"> {cur()} {totals.total.toFixed(2)}</span></div>
                      {totals.vatAmount > 0 && (
                        <div className="flex justify-between text-info"><span>VAT ({totals.vatRate}%):</span><span className="font-medium">{cur()} {totals.vatAmount.toFixed(2)}</span></div>
                      )}
                      <div className="flex justify-between text-lg font-bold border-t pt-1"><span>Total{totals.vatEnabled && totals.vatAmount > 0 ? ' (incl. VAT)' : ''}:</span><span className="text-primary"> {cur()} {(totals.vatAmount > 0 ? totals.totalWithVat : totals.total).toFixed(2)}</span></div>
                    </div>
                  </div>
                </div>

                {/* Credit-specific fields */}
                {formData.payment_mode === 'credit' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-warning/10 border border-warning/30 rounded-lg">
                    <div>
                      <Label className="text-xs">Reference / External Invoice #</Label>
                      <Input
                        value={formData.invoice_number_ref}
                        onChange={(e) => setFormData({ ...formData, invoice_number_ref: e.target.value })}
                        placeholder="e.g., PO-2024-0041"
                        data-testid="invoice-ref-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Due Date</Label>
                      <Input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        data-testid="invoice-due-date"
                      />
                    </div>
                  </div>
                )}

                <div><Label>Notes</Label><Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional" /></div>
                <div className="flex gap-3">
                  <Button type="submit" className="rounded-full" data-testid="submit-invoice">Create Invoice & Record Sale</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-full">Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* iter927 — Memoised table: typing in the form no longer re-renders it. */}
        <InvoicesTable
          filtered={filtered}
          selectedIds={selectedIds}
          salesById={salesById}
          zatcaPhase2={zatcaPhase2}
          companySettings={companySettings}
          uploadingId={uploadingId}
          emailingId={emailingId}
          mergingPdf={mergingPdf}
          h={{ handleMergedPdf, setSelectedIds, toggleSelect, setViewImage, handleImageUpload, openEditItems, setPrintInvoice, setQrData, submitZatca, setReceivingInvoice, setReceiveData, setShowReceiveDialog, handleDelete, handleEmailInvoice }}
        />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <PageSizeSelector value={invPageSize} onChange={setInvPageSize} testid="invoices-page-size" />
          {invoices.length < invoiceTotal && (
            <Button variant="outline" className="rounded-xl" onClick={loadMoreInvoices} data-testid="load-more-invoices-btn">
              Load More ({invoices.length} of {invoiceTotal})
            </Button>
          )}
        </div>

        {/* Receive Credit Dialog */}
        <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
          <DialogContent data-testid="receive-invoice-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Receive Credit - {receivingInvoice?.invoice_number}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Customer: {receivingInvoice?.customer_name || 'Walk-in'} | Outstanding: <span className="font-bold text-warning"> {cur()} {receivingInvoice?.credit_remaining?.toFixed(2)}</span></p>
            <form onSubmit={handleReceiveCredit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Payment Amount</Label><Input type="number" step="0.01" value={receiveData.amount} onChange={(e) => setReceiveData({ ...receiveData, amount: e.target.value })} placeholder="0.00" /></div>
                <div><Label>Discount</Label><Input type="number" step="0.01" value={receiveData.discount} onChange={(e) => setReceiveData({ ...receiveData, discount: e.target.value })} placeholder="0.00" /></div>
              </div>
              {(parseFloat(receiveData.amount) > 0 || parseFloat(receiveData.discount) > 0) && (
                <div className="p-3 bg-secondary/50 rounded-lg space-y-1 text-sm">
                  <div className="flex justify-between"><span>Payment:</span><span> {cur()} {(parseFloat(receiveData.amount) || 0).toFixed(2)}</span></div>
                  {parseFloat(receiveData.discount) > 0 && <div className="flex justify-between"><span>Discount:</span><span className="text-error">-SR {(parseFloat(receiveData.discount) || 0).toFixed(2)}</span></div>}
                  <div className="flex justify-between border-t pt-1 font-bold"><span>Total Settled:</span><span className="text-success"> {cur()} {((parseFloat(receiveData.amount) || 0) + (parseFloat(receiveData.discount) || 0)).toFixed(2)}</span></div>
                </div>
              )}
              <div><Label>Payment Mode</Label>
                <Select value={receiveData.payment_mode} onValueChange={(v) => setReceiveData({ ...receiveData, payment_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="rounded-full">Receive Payment</Button>
                <Button type="button" variant="outline" onClick={() => setShowReceiveDialog(false)} className="rounded-full">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ZATCA Invoice Print Dialog */}
        <Dialog open={!!printInvoice} onOpenChange={(v) => !v && setPrintInvoice(null)}>
          <DialogContent className="max-w-lg" data-testid="print-invoice-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Tax Invoice / فاتورة ضريبية</DialogTitle></DialogHeader>
            {printInvoice && (
              <div id="invoice-print-area">
                <div className="border rounded-lg p-4 space-y-3 text-sm bg-white" data-testid="invoice-preview">
                  {/* Header */}
                  <div className="text-center border-b pb-3">
                    <p className="text-lg font-bold">{companySettings.company_name || 'Smarto'}</p>
                    <p className="text-xs text-muted-foreground">{companySettings.address || ''}</p>
                    {companySettings.vat_number && <p className="text-xs mt-1">VAT No / الرقم الضريبي: <strong>{companySettings.vat_number}</strong></p>}
                    <p className="text-base font-bold mt-2 border-y py-1">TAX INVOICE / فاتورة ضريبية</p>
                  </div>
                  {/* Invoice Info */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <div>Invoice No / رقم الفاتورة: <strong>{printInvoice.invoice_number}</strong></div>
                    <div className="text-right">Date / التاريخ: <strong>{new Date(printInvoice.date).toLocaleDateString('en-GB')}</strong></div>
                    <div>Customer / العميل: <strong>{printInvoice.customer_name || 'Walk-in / عميل نقدي'}</strong></div>
                    <div className="text-right">Payment / الدفع: <strong>{printInvoice.payment_mode}</strong></div>
                    {printInvoice.buyer_vat_number && <div>Buyer VAT / ضريبة المشتري: <strong>{printInvoice.buyer_vat_number}</strong></div>}
                  </div>
                  {/* Items Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border">
                      <thead><tr className="bg-muted border-b">
                        <th className="p-1.5 text-left"># </th>
                        <th className="p-1.5 text-left">Description / الوصف</th>
                        <th className="p-1.5 text-center">Qty / الكمية</th>
                        <th className="p-1.5 text-right">Price / السعر</th>
                        <th className="p-1.5 text-right">Total / المجموع</th>
                      </tr></thead>
                      <tbody>
                        {(printInvoice.items || []).map((item, i) => (
                          <tr key={i} className="border-b"><td className="p-1.5">{i + 1}</td>
                            <td className="p-1.5">{item.description}</td>
                            <td className="p-1.5 text-center">{item.quantity}</td>
                            <td className="p-1.5 text-right">{item.unit_price?.toFixed(2)}</td>
                            <td className="p-1.5 text-right">{item.total?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Totals */}
                  <div className="space-y-1 text-xs border-t pt-2">
                    <div className="flex justify-between"><span>Subtotal / المجموع الفرعي</span><span>{cur()} {printInvoice.subtotal?.toFixed(2)}</span></div>
                    {printInvoice.discount > 0 && <div className="flex justify-between text-error"><span>Discount / الخصم</span><span>-SR {printInvoice.discount?.toFixed(2)}</span></div>}
                    <div className="flex justify-between"><span>Taxable Amount / المبلغ الخاضع للضريبة</span><span>{cur()} {printInvoice.total?.toFixed(2)}</span></div>
                    {(printInvoice.vat_amount || 0) > 0 && (
                      <div className="flex justify-between text-info font-medium"><span>VAT ({printInvoice.vat_rate || 15}%) / ضريبة القيمة المضافة</span><span>{cur()} {printInvoice.vat_amount?.toFixed(2)}</span></div>
                    )}
                    <div className="flex justify-between text-base font-bold border-t pt-1"><span>Total / الإجمالي</span><span>{cur()} {(printInvoice.total_with_vat || printInvoice.total)?.toFixed(2)}</span></div>
                  </div>
                  {/* QR Code */}
                  {qrData && (
                    <div className="flex items-center gap-3 border-t pt-2">
                      <QRCodeSVG value={qrData} size={80} data-testid="zatca-qr" />
                      <div className="text-[10px] text-muted-foreground">
                        <p className="font-medium">ZATCA QR / رمز هيئة الزكاة</p>
                        <p>Seller / البائع: {companySettings.company_name || 'Smarto'}{companySettings.company_name_ar ? ` — ${companySettings.company_name_ar}` : ''}</p>
                        {companySettings.vat_number && <p>VAT No: {companySettings.vat_number}</p>}
                      </div>
                    </div>
                  )}
                  {/* Attached Image */}
                  {printInvoice.image_url && (
                    <div className="border-t pt-2 mt-2">
                      <p className="text-xs font-medium mb-1">Attached Image / صورة مرفقة</p>
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${printInvoice.image_url}`} alt="Invoice attachment" className="max-w-full max-h-48 rounded border" />
                    </div>
                  )}
                  {/* Powered by SMARTO footer */}
                  <div className="border-t mt-4 pt-2 flex items-center justify-center gap-1.5" data-testid="powered-by-footer">
                    <img src="/smarto-icon.png" alt="" className="h-4 w-4 object-contain" />
                    <span className="text-[10px] text-muted-foreground">Powered by <span className="font-bold text-muted-foreground">SMARTO</span></span>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button className="flex-1 rounded-xl" data-testid="print-btn" onClick={async () => {
                    // iter892 — Route via printSummary so Android tablets with a
                    // USB/BT/Sunmi thermal printer print via the native bridge
                    // instead of Android's Default Print Service (which is blind
                    // to receipt printers). Desktop / preview still get the same
                    // browser-print-dialog experience via the iframe fallback.
                    const area = document.getElementById('invoice-print-area');
                    const htmlFallback = area ? `<html><head><title>${printInvoice.invoice_number}</title><style>body{font-family:Arial,sans-serif;padding:20px;font-size:12px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:4px}th{background:#f5f5f5;text-align:left}.text-right{text-align:right}.text-center{text-align:center}.bold{font-weight:bold}.border-t{border-top:1px solid #ddd;padding-top:4px;margin-top:4px}svg{display:block}.powered-footer{text-align:center;border-top:1px solid #eee;margin-top:16px;padding-top:8px;font-size:9px;color:#999}</style></head><body>${area.innerHTML}<script>window.onload=()=>{setTimeout(()=>{window.print();},150)}</script></body></html>` : null;
                    // Best-effort printer fetch (only needed for the Android
                    // thermal path; the fallback works without it).
                    let printers = [];
                    try {
                      const { data } = await api.get('/printers');
                      printers = Array.isArray(data) ? data : (data?.items || []);
                    } catch { /* no printers configured — fallback path handles it */ }
                    const payload = {
                      company: companySettings.company_name || 'Smarto',
                      title: `INVOICE ${printInvoice.invoice_number}`,
                      order_number: printInvoice.invoice_number,
                      date: printInvoice.date ? new Date(printInvoice.date).toLocaleDateString('en-GB') : '',
                      currency: cur(),
                      items: (printInvoice.items || []).map(it => ({
                        qty: it.quantity, name: it.description || it.name || '',
                        subtotal: Number(it.subtotal || (it.quantity * (it.unit_price || 0)) || 0),
                      })),
                      subtotal: Number(printInvoice.subtotal || 0),
                      tax: Number(printInvoice.vat_amount || 0),
                      total: Number(printInvoice.total_with_vat || printInvoice.total || 0),
                      footer: printInvoice.customer_name ? `Customer: ${printInvoice.customer_name}` : 'Thank you',
                    };
                    const { printSummary } = await import('@/lib/printSummary');
                    await printSummary({
                      printers,
                      branchId: user?.branch_id || printInvoice.branch_id,
                      title: `Invoice ${printInvoice.invoice_number}`,
                      payload,
                      htmlFallback,
                    });
                  }}><Printer size={14} className="mr-1" />Print</Button>
                  <Button variant="outline" className="rounded-xl" data-testid="invoice-pdf-dialog-btn" onClick={() => exportInvoiceToPDF(printInvoice, companySettings, qrData, cur())}>
                    <FileDown size={14} className="mr-1" />PDF
                  </Button>
                  <Button variant="outline" className="rounded-xl" onClick={() => setPrintInvoice(null)}>Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Image Viewer Dialog */}
        <Dialog open={!!viewImage} onOpenChange={(v) => !v && setViewImage(null)}>
          <DialogContent className="max-w-lg" data-testid="image-viewer-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Invoice Image - {viewImage?.invoice_number}</DialogTitle></DialogHeader>
            {viewImage && (
              <div className="space-y-3">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${viewImage.image_url}`}
                  alt={`Invoice ${viewImage.invoice_number}`}
                  className="w-full rounded-lg border"
                  data-testid="invoice-image-preview"
                />
                <div className="flex gap-2">
                  <label className="flex-1 cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                      if (e.target.files[0]) {
                        handleImageUpload(viewImage.id, e.target.files[0]);
                        setViewImage(null);
                      }
                    }} />
                    <Button variant="outline" className="w-full rounded-xl" asChild><span><Upload size={14} className="mr-1" />Replace</span></Button>
                  </label>
                  <Button variant="outline" className="rounded-xl text-error hover:text-error" data-testid="delete-invoice-image"
                    onClick={() => { handleDeleteImage(viewImage.id); setViewImage(null); }}>
                    <Trash2 size={14} className="mr-1" />Remove
                  </Button>
                  <Button variant="outline" className="rounded-xl" onClick={() => setViewImage(null)}>Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Invoice Items Dialog */}
        <Dialog open={!!editInvoice} onOpenChange={(v) => !v && setEditInvoice(null)}>
          <DialogContent className="max-w-2xl" data-testid="edit-items-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">Edit Items - {editInvoice?.invoice_number}</DialogTitle>
            </DialogHeader>
            {editInvoice && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Customer: {editInvoice.customer_name || 'Walk-in'} | Date: {format(new Date(editInvoice.date), 'MMM dd, yyyy')}
                </p>
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-muted/50 border-b">
                        <th className="p-2.5 text-left text-xs font-semibold text-muted-foreground">Description</th>
                        <th className="p-2.5 text-center text-xs font-semibold text-muted-foreground w-20">Qty</th>
                        <th className="p-2.5 text-right text-xs font-semibold text-muted-foreground w-28">Unit Price</th>
                        <th className="p-2.5 text-right text-xs font-semibold text-muted-foreground w-28">Total</th>
                        <th className="p-2.5 w-16"></th>
                      </tr></thead>
                      <tbody>
                        {editItems.map((item, i) => (
                          <tr key={i} className="border-b hover:bg-muted/50">
                            <td className="p-2.5 font-medium text-foreground">{item.description}</td>
                            <td className="p-2.5 text-center">
                              <Input type="number" value={item.quantity} min="1"
                                onChange={(e) => {
                                  const items = [...editItems];
                                  items[i] = { ...items[i], quantity: parseFloat(e.target.value) || 1, total: (parseFloat(e.target.value) || 1) * items[i].unit_price };
                                  setEditItems(items);
                                }}
                                className="h-8 w-16 text-center mx-auto" />
                            </td>
                            <td className="p-2.5 text-right text-muted-foreground">{cur()} {item.unit_price?.toFixed(2)}</td>
                            <td className="p-2.5 text-right font-semibold">{cur()} {((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}</td>
                            <td className="p-2.5 text-center">
                              <Button size="sm" variant="ghost" onClick={() => removeEditItem(i)}
                                className="h-7 w-7 p-0 text-error hover:text-error hover:bg-error/10"
                                disabled={editItems.length <= 1}
                                data-testid={`delete-item-${i}`}>
                                <Trash2 size={14} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="p-3 bg-muted/50 border-t space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal:</span>
                      <span className="font-medium">{cur()} {editItems.reduce((s, i) => s + (i.quantity || 1) * (i.unit_price || 0), 0).toFixed(2)}</span>
                    </div>
                    {editInvoice.discount > 0 && (
                      <div className="flex justify-between text-error">
                        <span>Discount:</span>
                        <span>-SR {editInvoice.discount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-base border-t pt-1">
                      <span>Total:</span>
                      <span className="text-foreground">{cur()} {(editItems.reduce((s, i) => s + (i.quantity || 1) * (i.unit_price || 0), 0) - (editInvoice.discount || 0)).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button onClick={saveEditItems} className="rounded-full" data-testid="save-items-btn">Save Changes</Button>
                  <Button variant="outline" onClick={() => setEditInvoice(null)} className="rounded-full">Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
